import { PicklistSourceId } from "../../Picklists/IPicklistsProvider";
import { LocalizedString } from "../../Localization/ILocalizer";
import { ViewModel } from "../../CoreTypes";
import {
    FilterBuilder,
    isResolveToAllFilter,
    isResolveToNoneFilter,
    resolveToAllFilter,
    ResolveToAllFilter,
    resolveToNoneFilter,
    ResolveToNoneFilter,
} from "../../../../Controls/ValueEditors/Picklist/Common/FilterBuilder";
import { PicklistDataItem, PicklistQueryResponse } from "../../Picklists/PicklistTypes";
import {
    DigestFilterColumn,
    DigestFilterOperator,
    DigestLogicalOperator,
    DigestQueryFilter,
} from "../../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { buildFilterPredicate } from "../../Picklists/GPathFilter";
import { falsePredicate, Predicate, truePredicate } from "../../../../../../Common/Funcs";
import { reject } from "../../../../../../Common/TypingUtils";

import { ValueValidatorBase } from "./Bases/ValueValidatorBase";
import { CheckType, IValidationContext, ValidationResult, ValidationResults } from "./Bases/IValueValidator";

type BuildFilterCallback = (
    builder: FilterBuilder,
    getPath: (path: string) => string,
    none: ResolveToNoneFilter,
    all: ResolveToAllFilter
) => null | undefined | FilterBuilder | ResolveToNoneFilter | ResolveToAllFilter;

export class DigestConditionValueValidator extends ValueValidatorBase {
    private readonly picklistSourceId: PicklistSourceId;
    private readonly condition: BuildFilterCallback;
    private readonly checkType: CheckType;
    private readonly message: undefined | LocalizedString;

    public constructor(
        picklistSourceId: PicklistSourceId,
        condition: BuildFilterCallback,
        checkType: CheckType,
        message: undefined | LocalizedString
    ) {
        super();
        this.picklistSourceId = picklistSourceId;
        this.checkType = checkType;
        this.condition = condition;
        this.message = message;
    }

    public async validate(context: IValidationContext, nodeViewModel: ViewModel): Promise<ValidationResult> {
        if (nodeViewModel.value == undefined) {
            return ValidationResults.Valid;
        }

        let digestCondition: Predicate<PicklistDataItem>;
        const conditionBuilder = this.condition(
            new FilterBuilder(),
            () => reject("Path expressions is not allowed in digestcondition"),
            resolveToNoneFilter,
            resolveToAllFilter
        );
        if (conditionBuilder == undefined || isResolveToAllFilter(conditionBuilder)) {
            digestCondition = truePredicate;
        } else if (isResolveToNoneFilter(conditionBuilder)) {
            digestCondition = falsePredicate;
        } else {
            digestCondition = buildFilterPredicate(conditionBuilder.build());
        }

        const values: readonly string[] = Array.isArray(nodeViewModel.value)
            ? nodeViewModel.value
            : [nodeViewModel.value];

        const data = await this.getValuesFromPicklist(values, context, values.length);
        const items = data?.items ?? [];
        if (items.length > 0) {
            if (!items.some(x => digestCondition(x))) {
                const description =
                    this.getMessage(context, this.message) ?? "Значение справочника не соотвествует заданным условиям";
                return ValidationResults.Invalid(context.localizer.translate(description), this.checkType);
            }
        }
        return ValidationResults.Valid;
    }

    private async getValuesFromPicklist(
        values: readonly string[],
        context: IValidationContext,
        take: number
    ): Promise<undefined | PicklistQueryResponse> {
        const filter: DigestQueryFilter = {
            filter: {
                operator: DigestLogicalOperator.Or,
                operands: values.map(
                    (value): DigestFilterColumn => ({
                        operator: DigestFilterOperator.Equal,
                        column: "code",
                        value: value ?? "",
                    })
                ),
            },
            take: take,
        };
        return context.picklistsProvider.getPicklist(this.picklistSourceId)?.query(filter);
    }
}
